import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import * as React from "react";

import {
  addDealNote,
  type Deal,
  type DealActivityType,
  type DealNoteCreatePayload,
} from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { Button } from "@/components/ui/button";
import { DateRowInput } from "@/components/ui/date-row-input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TimeRowInput } from "@/components/ui/time-row-input";
import { dealToasts, useToast } from "@/components/ui/toast";
import { FormError } from "@/components/ui/form-error";

const INPUT_CLASS =
  "flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50";

/** The dropdown leaf options grouped by category (CHP-429). The `value` is the
 * `subtype` persisted in `activity_details`; the `type` is the stored
 * `activity_type` (category), which decides which dynamic fields show. */
const ACTIVITY_OPTIONS: {
  group: string;
  type: DealActivityType;
  options: { value: string; label: string }[];
}[] = [
  {
    group: "Meetings",
    type: "meeting",
    options: [
      { value: "initial_discovery_meeting", label: "Initial Discovery Meeting" },
      { value: "follow_up_meeting", label: "Follow-up Meeting" },
    ],
  },
  {
    group: "Communications",
    type: "communication",
    options: [
      { value: "phone_call", label: "Phone Call" },
      { value: "email_exchange", label: "Email Exchange" },
    ],
  },
  {
    group: "Outreach & Admin",
    type: "outreach_admin",
    options: [
      { value: "cold_outreach", label: "Cold Outreach" },
      { value: "no_response_attempted_contact", label: "No Response/Attempted Contact" },
    ],
  },
];

/** Lookup: subtype value -> { type (category), label }. */
const SUBTYPE_INDEX: Record<string, { type: DealActivityType; label: string }> = Object.fromEntries(
  ACTIVITY_OPTIONS.flatMap((g) =>
    g.options.map((o) => [o.value, { type: g.type, label: o.label }] as const),
  ),
);

/** First subtype — the default selection. */
const DEFAULT_SUBTYPE = ACTIVITY_OPTIONS[0]!.options[0]!.value;

interface AttendeeRow {
  name: string;
  title: string;
}

/** Today as YYYY-MM-DD (local). */
function todayValue(): string {
  const now = new Date();
  const off = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - off).toISOString().slice(0, 10);
}

/** Current time as HH:MM (local). */
function nowTimeValue(): string {
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

interface Props {
  deal: Deal;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * "Log deal progress" modal (CHP-429). An activity-log entry for a live deal:
 * a categorised dropdown (Meetings / Communications / Outreach & Admin) whose
 * leaf decides the dynamic fields. Meetings capture Date, Time, Attendees, Key
 * Discussion Points, and Next Steps; Communications / Outreach & Admin capture
 * Date and a simplified Details area. Date/Time auto-populate to now. The draft
 * survives a premature close (state is reset only on a successful submit), and
 * each submission appends a DealNote with `activity_type`/`activity_details`
 * that the "Deal Progress" section then renders.
 */
export function LogDealProgressModal({ deal, open, onOpenChange }: Props) {
  const { accessToken } = useCurrentUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [subtype, setSubtype] = React.useState<string>(DEFAULT_SUBTYPE);
  const [date, setDate] = React.useState<string>(() => todayValue());
  const [time, setTime] = React.useState<string>(() => nowTimeValue());
  const [attendees, setAttendees] = React.useState<AttendeeRow[]>([{ name: "", title: "" }]);
  const [keyDiscussionPoints, setKeyDiscussionPoints] = React.useState("");
  const [nextSteps, setNextSteps] = React.useState("");
  const [details, setDetails] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);

  const category = SUBTYPE_INDEX[subtype]!.type;
  const isMeeting = category === "meeting";

  function resetForm() {
    setSubtype(DEFAULT_SUBTYPE);
    setDate(todayValue());
    setTime(nowTimeValue());
    setAttendees([{ name: "", title: "" }]);
    setKeyDiscussionPoints("");
    setNextSteps("");
    setDetails("");
    setError(null);
  }

  const log = useMutation({
    mutationFn: () => {
      const label = SUBTYPE_INDEX[subtype]!.label;
      let payload: DealNoteCreatePayload;
      if (isMeeting) {
        // CHP-464 (finishing CHP-409): title is mandatory — sent as-is, never
        // dropped to undefined. Validation below guarantees it's non-empty for
        // every named attendee before submit enables.
        const cleanedAttendees = attendees
          .map((a) => ({ name: a.name.trim(), title: a.title.trim() }))
          .filter((a) => a.name.length > 0);
        // Human-readable body so the entry still reads sensibly anywhere a
        // plain note would (audit, manager view): the renderer uses the
        // structured activity_details, but body is the durable fallback.
        const attendeeLine = cleanedAttendees
          .map((a) => (a.title ? `${a.name} (${a.title})` : a.name))
          .join(", ");
        payload = {
          body: `${label} on ${date} at ${time}`,
          activity_type: category,
          activity_details: {
            subtype,
            date,
            time,
            attendees: cleanedAttendees,
            key_discussion_points: keyDiscussionPoints.trim(),
            next_steps: nextSteps.trim(),
          },
        };
        // Keep the body informative without ballooning it.
        if (attendeeLine) payload.body += ` with ${attendeeLine}`;
      } else {
        payload = {
          body: `${label} on ${date}`,
          activity_type: category,
          activity_details: {
            subtype,
            date,
            details: details.trim(),
          },
        };
      }
      return addDealNote(accessToken!, deal.id, payload);
    },
    onSuccess: () => {
      resetForm();
      onOpenChange(false);
      toast(dealToasts.progressLogged());
      void queryClient.invalidateQueries({ queryKey: ["deals", "notes", deal.id] });
    },
    onError: () => setError("Couldn't log progress. Refresh and try again."),
  });

  // CHP-464 (finishing CHP-409): an attendee row counts only when BOTH name
  // and title are filled, and a named-but-untitled row BLOCKS submit (silently
  // dropping it would lose data the form appeared to accept).
  const namedAttendees = attendees.filter((a) => a.name.trim().length > 0);
  const attendeesComplete =
    namedAttendees.length > 0 && namedAttendees.every((a) => a.title.trim().length > 0);
  const submitDisabled =
    !date ||
    (isMeeting
      ? !time || !attendeesComplete || !keyDiscussionPoints.trim() || !nextSteps.trim()
      : !details.trim());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Log deal progress</DialogTitle>
          <DialogDescription>
            Record an activity against <span className="font-medium">{deal.client_name}</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="activity-type">Activity type *</Label>
            <select
              id="activity-type"
              data-testid="activity-type"
              value={subtype}
              onChange={(e) => setSubtype(e.target.value)}
              className={INPUT_CLASS}
            >
              {ACTIVITY_OPTIONS.map((g) => (
                <optgroup key={g.group} label={g.group}>
                  {g.options.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="progress-date">Date *</Label>
              <DateRowInput
                id="progress-date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            {isMeeting ? (
              <div className="space-y-1">
                <Label htmlFor="progress-time">Time *</Label>
                <TimeRowInput
                  id="progress-time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                />
              </div>
            ) : null}
          </div>

          {isMeeting ? (
            <>
              {/* CHP-409 carry-over: the "Name" / "Title" headers show ONCE, not
                  per attendee row. */}
              <fieldset className="space-y-2">
                <legend className="text-sm font-semibold">Attendees</legend>
                <div className="flex items-end gap-2">
                  <span className="flex-1 text-xs font-medium text-muted-foreground">Name *</span>
                  {/* CHP-464 (finishing CHP-409): Title is mandatory, not optional. */}
                  <span className="flex-1 text-xs font-medium text-muted-foreground">Title *</span>
                  {/* Spacer matching the remove-button column width. */}
                  <span className="w-9" aria-hidden />
                </div>
                <div className="space-y-2">
                  {attendees.map((row, idx) => (
                    <div key={idx} className="flex items-end gap-2">
                      <input
                        type="text"
                        aria-label={`Attendee ${idx + 1} name`}
                        value={row.name}
                        onChange={(e) =>
                          setAttendees((rows) =>
                            rows.map((r, i) => (i === idx ? { ...r, name: e.target.value } : r)),
                          )
                        }
                        className={`flex-1 ${INPUT_CLASS}`}
                      />
                      <input
                        type="text"
                        aria-label={`Attendee ${idx + 1} title`}
                        value={row.title}
                        onChange={(e) =>
                          setAttendees((rows) =>
                            rows.map((r, i) => (i === idx ? { ...r, title: e.target.value } : r)),
                          )
                        }
                        className={`flex-1 ${INPUT_CLASS}`}
                      />
                      {attendees.length > 1 ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-9 w-9 p-0 text-muted-foreground"
                          aria-label={`Remove attendee ${idx + 1}`}
                          onClick={() => setAttendees((rows) => rows.filter((_, i) => i !== idx))}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden />
                        </Button>
                      ) : (
                        <span className="w-9" aria-hidden />
                      )}
                    </div>
                  ))}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="add-attendee"
                  onClick={() => setAttendees((rows) => [...rows, { name: "", title: "" }])}
                >
                  + Add attendee
                </Button>
              </fieldset>

              <div className="space-y-1">
                <Label htmlFor="key-discussion-points">Key discussion points *</Label>
                <Textarea
                  id="key-discussion-points"
                  value={keyDiscussionPoints}
                  onChange={(e) => setKeyDiscussionPoints(e.target.value)}
                  maxLength={4000}
                  rows={3}
                  placeholder="Briefly outline what was discussed."
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="next-steps">Next steps *</Label>
                <Textarea
                  id="next-steps"
                  value={nextSteps}
                  onChange={(e) => setNextSteps(e.target.value)}
                  maxLength={4000}
                  rows={3}
                  placeholder="What is the follow-up action?"
                />
              </div>
            </>
          ) : (
            <div className="space-y-1">
              <Label htmlFor="progress-details">Details *</Label>
              <Textarea
                id="progress-details"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                maxLength={4000}
                rows={4}
                placeholder="Summarise the activity."
              />
            </div>
          )}
        </div>

        {error ? <FormError>{error}</FormError> : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={log.isPending}>
            Cancel
          </Button>
          <Button
            data-testid="submit-deal-progress"
            onClick={() => {
              setError(null);
              log.mutate();
            }}
            disabled={submitDisabled}
            loading={log.isPending}
            loadingLabel="Saving…"
          >
            Log progress
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
